import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useDebouncedCallback } from 'use-debounce';

export type Validator = (value: any) => string | null;

type InputArgs<ValueType, ItemType> = {
  // <T extends object>
  name?: string;
  initialValue: ValueType;
  label?: string;
  inputMinCharCount?: number;
  validator?: Validator;
  validate?: boolean;
  isInvalid?: boolean;
  isDisabled?: boolean;
  errorMessage?: string;
  onChange?(item: ValueType): void;
  prepareItems(search: string): Promise<ItemType[] | null | undefined>;
  renderItem(item: ItemType): React.ReactNode;
  renderValue(item: ValueType): string;
};

type OutputArgs<ValueType, ItemType> = {
  //<ValueType, T extends object>
  name?: string;
  value: ValueType;
  setValue(value: ValueType): void;
  items: ItemType[];
  label?: string;
  inputMinCharCount?: number;
  isLoading: boolean;
  isInvalid?: boolean;
  isDisabled?: boolean;
  errorMessage?: string;
  validator?: Validator;
  renderItem(item: ItemType): React.ReactNode;
  renderValue(item: ValueType): string;
  onInputChange(e: any, eventData: { value: string }): void;
  onChange(e: React.SyntheticEvent<Element, Event>, eventData: { value: any }): void;
};

export function useSelect<ValueType, ItemType>({
  name,
  initialValue,
  label,
  inputMinCharCount = 0,
  validator,
  validate,
  isInvalid: isExternalInvalid,
  errorMessage: externalErrorMessage,
  onChange: onChangeCallback,
  renderItem,
  prepareItems,
  renderValue,
  ...rest
}: InputArgs<ValueType, ItemType>): OutputArgs<ValueType, ItemType> {
  const [items, setItems] = useState<ItemType[]>([]);
  const [value, setValue] = useState<ValueType>(initialValue);
  const isInvalidRef = useRef(false);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    setValue(initialValue);
  }, [initialValue]);

  const fetchData = useDebouncedCallback(async (value: string) => {
    const data = await prepareItems(value);
    if (data) {
      setItems(data);
      setIsLoading(false);
    }
  }, 300);

  // load all data after start
  useEffect(() => {
    onInputChange({} as React.ChangeEvent, { value: '' });
  }, [fetchData]);

  const onInputChange = (e: React.ChangeEvent, eventData: { value: string }) => {
    setItems([]);
    if (eventData.value.length >= inputMinCharCount) {
      setIsLoading(true);
      fetchData(eventData.value);
    }
  };

  const onChange = useCallback(
    (e: React.SyntheticEvent, eventData: { value: any }) => {
      onChangeCallback?.(eventData.value);
      setValue(eventData.value);
    },
    [onChangeCallback],
  );

  const validationResult = useMemo(() => {
    if (validator && (validate || isInvalidRef.current)) {
      const errorMessage = validator(value);
      const isInvalid = !!errorMessage;
      isInvalidRef.current = isInvalid;

      return {
        isInvalid,
        errorMessage: errorMessage ?? '',
      };
    }
    return {
      isInvalid: false,
      errorMessage: '',
    };
  }, [value, validator, validate]);

  // When the component goes to be unmounted.
  useEffect(
    () => () => {
      fetchData.cancel();
    },
    [fetchData],
  );

  return {
    ...rest,
    isInvalid: validationResult.isInvalid || isExternalInvalid,
    errorMessage: validationResult.errorMessage || externalErrorMessage,
    name,
    value,
    setValue,
    label,
    items,
    inputMinCharCount,
    isLoading,
    renderItem,
    renderValue,
    onInputChange,
    onChange,
    validator,
  };
}
